import React, { useState } from "react";
import { Button } from "./ui/button";
import { CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";

/**
 * StripePayButton — initiates a Stripe Checkout Session for the given booking
 * and redirects the customer to Stripe's hosted checkout page.
 *
 * Props:
 *  - bookingId: string (required)
 *  - amount: number  (€, display only — actual amount comes from backend)
 *  - onError?: (msg) => void
 */
export function StripePayButton({ bookingId, amount, onError }) {
  const { lang } = useApp();
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    try {
      setLoading(true);
      // No origin_url: the backend derives return URLs from
      // PUBLIC_FRONTEND_URL, so a client cannot redirect a real payment.
      const { data } = await api.post(`/payments/${bookingId}/checkout`, {});
      if (!data?.url) throw new Error("no_url");
      // Redirect to Stripe hosted checkout
      window.location.href = data.url;
    } catch (e) {
      setLoading(false);
      const code = e?.response?.data?.detail?.code;
      const msg = code === "payment_expired"
        ? lang === "en"
          ? "The payment window has expired: this reservation was cancelled."
          : "Il tempo per il pagamento è scaduto: la prenotazione è stata annullata."
        : code === "already_paid"
          ? lang === "en"
            ? "This booking is already paid."
            : "Questa prenotazione è già stata pagata."
          : lang === "en"
            ? "Could not start payment. Please try again."
            : "Impossibile avviare il pagamento. Riprova.";
      toast.error(msg);
      if (onError) onError(msg, code);
    }
  };

  return (
    <div data-testid="stripe-pay-button-wrapper" className="space-y-2">
      <Button
        type="button"
        onClick={handleClick}
        disabled={loading}
        data-testid="stripe-pay-button"
        className="h-12 w-full gap-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <CreditCard className="h-4 w-4" />
        )}
        {lang === "en" ? `Pay €${amount} now` : `Paga €${amount} ora`}
      </Button>
      <p className="text-center text-xs text-muted-foreground">
        {lang === "en" ? "Secure payment via Stripe" : "Pagamento sicuro tramite Stripe"}
      </p>
    </div>
  );
}
